/**
 * Report History Hook
 *
 * Tracks report execution history using Zustand with localStorage persistence.
 * Maintains last 100 report executions with details like timestamp, user, record count, and duration.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ReportExecution {
  id: string; // Unique execution ID
  reportId: string; // Report that was executed
  reportName: string; // Report name for display
  timestamp: string; // ISO timestamp of execution
  user: string; // User who executed the report
  recordCount: number; // Number of records returned
  duration: number; // Execution time in milliseconds
  objectName: string; // Object the report ran against
}

interface ReportHistoryState {
  executions: ReportExecution[];
  addExecution: (execution: Omit<ReportExecution, 'id'>) => void;
  getHistoryForReport: (reportId: string) => ReportExecution[];
  getAllHistory: () => ReportExecution[];
  clearHistory: () => void;
}

/**
 * Zustand store for report execution history
 * Persists to localStorage and keeps last 100 executions
 */
export const useReportHistory = create<ReportHistoryState>()(
  persist(
    (set, get) => ({
      executions: [],

      /**
       * Add a new report execution to history
       * Automatically prunes to last 100 executions
       */
      addExecution: (execution) => {
        const id = `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newExecution: ReportExecution = { id, ...execution };

        set((state) => {
          const updated = [newExecution, ...state.executions];
          // Keep only last 100 executions
          return { executions: updated.slice(0, 100) };
        });
      },

      /**
       * Get execution history for a specific report
       */
      getHistoryForReport: (reportId) => {
        return get().executions.filter(exec => exec.reportId === reportId);
      },

      /**
       * Get all execution history
       */
      getAllHistory: () => {
        return get().executions;
      },

      /**
       * Clear all execution history
       */
      clearHistory: () => {
        set({ executions: [] });
      }
    }),
    {
      name: 'crm-report-history', // localStorage key
      version: 1, // For future migrations
    }
  )
);

/**
 * Helper hook to track report execution time
 * Usage:
 *   const tracker = useReportExecutionTracker();
 *   const startTime = tracker.start();
 *   // ... execute report ...
 *   const duration = tracker.end(startTime);
 */
export const useReportExecutionTracker = () => {
  return {
    start: () => Date.now(),
    end: (startTime: number) => Date.now() - startTime,
  };
};
